export const FAVORITES_STORAGE_KEY = 'vegai.favorites.v1';
export const HISTORY_STORAGE_KEY = 'vegai.history.v1';
export const MAX_FAVORITES = 100;
export const MAX_HISTORY_ENTRIES = 50;
export const RECENT_HISTORY_DUPLICATE_MS = 5 * 60 * 1000;

const isNullLikeLabel = (value) => {
  const normalized = String(value || '').trim().toLowerCase();
  return normalized === 'null' || normalized === 'undefined';
};

const normalizeList = (xs) => Array.isArray(xs)
  ? xs.map((x) => String(x || '').trim().toLowerCase()).filter(Boolean).sort().join(',')
  : '';

const cleanText = (value, max = 180) => String(value || '').trim().slice(0, max);

export const favoriteSignatureFor = (item = {}) => [
  item.mode,
  cleanText(item.input).toLowerCase(),
  cleanText(item.productMatch).toLowerCase(),
  normalizeList(item.rerunInput?.ingredients),
].join('|');

export const normalizeFavorites = (items) => {
  const seen = new Set();
  return [...(Array.isArray(items) ? items : [])]
    .sort((a, b) => (b.savedAt || b.ts || 0) - (a.savedAt || a.ts || 0))
    .reduce((acc, item) => {
      const cleaned = {
        ...item,
        input: cleanText(item.input, 500),
        productMatch: cleanText(item.productMatch, 180) || undefined,
        favoriteLabel: cleanText(item.favoriteLabel, 100) || undefined,
      };
      const signature = favoriteSignatureFor(cleaned);
      if (!signature.trim() || seen.has(signature)) return acc;
      seen.add(signature);
      acc.push(cleaned);
      return acc;
    }, [])
    .slice(0, MAX_FAVORITES);
};

export function createFavoriteEntry(entry, now = Date.now()) {
  return normalizeFavorites([{
    id: crypto.randomUUID?.() ?? String(now),
    savedAt: now,
    ts: now,
    ...entry,
  }])[0] || null;
}

const cleanProductMatch = (value) => isNullLikeLabel(value) ? undefined : value;

export const historySignatureFor = (item = {}) => [
  item.mode,
  item.input,
  item.status,
  String(item.historyLabel || '').trim().toLowerCase(),
  isNullLikeLabel(item.productMatch) ? '' : String(item.productMatch || '').trim().toLowerCase(),
  normalizeList(item.rerunInput?.ingredients),
  normalizeList(item.nonPlantBasedIngredients),
  normalizeList(item.questionableIngredients),
  normalizeList(item.allergens),
].join('|');

const ingredientScanSignatureFor = (item) => {
  if (item.mode !== 'scanner') return '';
  const ingredients = normalizeList(item.rerunInput?.ingredients);
  if (!ingredients) return '';
  return [
    item.mode,
    item.input,
    item.status,
    ingredients,
    normalizeList(item.nonPlantBasedIngredients),
    normalizeList(item.questionableIngredients),
    normalizeList(item.allergens),
  ].join('|');
};

const areRecentDuplicates = (a, b, now = Date.now()) => {
  const signature = historySignatureFor(a);
  const ingredientScanSignature = ingredientScanSignatureFor(a);
  const age = Math.abs((b.ts || now) - (a.ts || now));
  return age < RECENT_HISTORY_DUPLICATE_MS && (
    historySignatureFor(b) === signature ||
    (ingredientScanSignature && ingredientScanSignatureFor(b) === ingredientScanSignature)
  );
};

const mergeDuplicate = (existing, incoming) => ({
  ...existing,
  historyLabel: existing.historyLabel || incoming.historyLabel,
  productMatch: cleanProductMatch(existing.productMatch) || cleanProductMatch(incoming.productMatch),
  rerunInput: existing.rerunInput || incoming.rerunInput,
});

export const normalizeHistoryItems = (items) => {
  const sorted = [...(Array.isArray(items) ? items : [])].sort((a, b) => b.ts - a.ts);
  return sorted.reduce((acc, item) => {
    const cleaned = {
      ...item,
      productMatch: cleanProductMatch(item.productMatch),
    };
    const duplicateIndex = acc.findIndex((existing) => areRecentDuplicates(cleaned, existing));
    if (duplicateIndex >= 0) {
      acc[duplicateIndex] = mergeDuplicate(acc[duplicateIndex], cleaned);
      return acc;
    }
    acc.push(cleaned);
    return acc;
  }, []).slice(0, MAX_HISTORY_ENTRIES);
};

function safeHistoryInput(rawInput) {
  if (typeof rawInput === 'string') return rawInput.trim();
  if (rawInput && typeof rawInput === 'object') {
    return typeof rawInput.type === 'string' ? 'Ingredients photo' : '[photo]';
  }
  return '';
}

export function createHistoryEntry(entry, now = Date.now()) {
  return {
    id: crypto.randomUUID?.() ?? String(now),
    ts: now,
    ...entry,
    productMatch: cleanProductMatch(entry.productMatch),
    input: safeHistoryInput(entry.input),
  };
}

export function addHistoryEntryToItems(items, entry, now = Date.now()) {
  const incomingEntry = createHistoryEntry(entry, now);
  const recentDuplicate = items.find((item) => areRecentDuplicates(incomingEntry, item, now));
  if (recentDuplicate) {
    return items.map((item) => (
      item.id === recentDuplicate.id ? mergeDuplicate(item, incomingEntry) : item
    ));
  }
  return [incomingEntry, ...items].slice(0, MAX_HISTORY_ENTRIES);
}

export function updateHistoryTitleInItems(items, id, title) {
  const historyLabel = String(title || '').trim().slice(0, 80);
  return items.map((item) => (
    item.id === id
      ? { ...item, historyLabel: historyLabel || undefined }
      : item
  ));
}
